import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Box, Button, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  step1Schema,
  step2Schema,
  step3Schema,
} from "../../schema/signupSchema";
import { BUTTON_LABELS, USER_TYPE } from "../../constants/lang";
import StyledSignup from "./StyledSignup";
import StepperComponent from "../../components/CommonComponents/Stepper";
import { Link, useNavigate } from "react-router-dom";
import BasicInformation from "./BasicInformation";
import BusinessDetails from "./BusinessDetails";
import ContactSocials from "./ContactSocials";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEmailCheck,
  fetchSignup,
} from "../../store/reducers/authentication/apiThunk";
import {
  ToastNotifyError,
  ToastNotifySuccess,
} from "../../components/CommonComponents/Toast/ToastNotify";
import {
  clearSignupFormData,
  setSignupFormData,
} from "../../store/reducers/authentication";
import { ROUTE_PATH } from "../../constants/route";
import rollbar from "../../rollbarConfig";

const { SIGNUP_REGISTRATION_REQUEST, LOGIN } = ROUTE_PATH;

const SignUpForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setLoading] = useState(false);

  const validationSchema = [step1Schema, step2Schema, step3Schema];
  const signupFormData = useSelector(
    (state) => state?.authentication?.signupFormData
  );
  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    setFocus,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema[activeStep]),
    defaultValues: signupFormData,
  });

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        setFocus(firstErrorField);
        setTimeout(() => {
          const errorElement = document.getElementById(firstErrorField);
          if (errorElement) {
            errorElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 300);
      }
    }
  }, [errors, setFocus]);

  const steps = [
    <BasicInformation control={control} errors={errors} />,
    <BusinessDetails control={control} errors={errors} watch={watch} />,
    <ContactSocials control={control} errors={errors} />,
  ];

  const emailCheck = async (emailAddress = "") => {
    const data = {
      email: emailAddress,
    };
    const res = await dispatch(fetchEmailCheck(data));
    const { status } = res?.payload;
    if (status !== 200) {
      setError("emailAddress", {
        type: "manual",
        message: "Account already exists",
      });
      return false;
    } else {
      return true;
    }
  };
  const onSubmit = async (data) => {
    const emailIsAvailable = await emailCheck(data?.emailAddress);
    if (emailIsAvailable) {
      if (activeStep < steps?.length - 1) {
        setActiveStep((prev) => prev + 1);
        dispatch(setSignupFormData(data));
      } else {
        setLoading(true);
        const dataToSend = {
          email: data?.emailAddress,
          password: data?.password,
          user_type: USER_TYPE.SELLER,
          seller_profile: {
            seller_name: data?.sellerName,
            brand_name: data?.brandName,
            business_name: data?.businessName,
            business_address: {
              street_address: data?.streetAddress,
              city: data?.city,
              zip_code: data?.zipCode,
              country: data?.country,
            },
            email_address: data?.emailAddress,
            website_link: data?.website,
            instagram_link: data?.instagram,
            others_link: data?.other,
            apparel_type: data?.Appareltype,
            categories: data?.Categories,
            location_of_design: data?.locationStudio,
            manufacturing_location: data?.ManufacturLocation,
            inventory_storage_location: data?.inventoryStorage,
            is_approved: false,
          },
        };
        try {
          const response = await dispatch(fetchSignup(dataToSend));
          const { status, message } = response?.payload || {};
          if (status) {
            ToastNotifySuccess("Registration is successful");
            dispatch(clearSignupFormData());
            navigate(SIGNUP_REGISTRATION_REQUEST);
          } else {
            ToastNotifyError(message);
          }
        } catch (error) {
          ToastNotifyError("An error occurred. Please try again.");
          rollbar.error("Error message", error);
        } finally {
          setLoading(false);
        }
      }
    } else {
      setError("emailAddress", {
        type: "manual",
        message: "Account already exists",
      });
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };
  return (
    <StyledSignup>
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        className='signup-wrap'
      >
        <Typography className='signup-header'>Sign up</Typography>
        <Typography className='signup-sub-header'>
          Join us to showcase your brand and grow your business!
        </Typography>
        <StepperComponent
          steps={[
            "Basic Information",
            "Business Details",
            "Connect Your Socials",
          ]}
          className='stepper'
          activeStep={activeStep}
        />
        <Box>{steps[activeStep]}</Box>
        <Box>
          <Button
            disabled={isLoading}
            type='submit'
            variant='contained'
            color='primary'
            className='submit-btn'
            aria-label='submit'
          >
            {activeStep === steps?.length - 1
              ? BUTTON_LABELS.SEND_REQUEST
              : BUTTON_LABELS.SAVE_AND_CONTINUE}
          </Button>
          {activeStep === 0 && (
            <Typography className='haveAccount'>
              Already have an account?{" "}
              <Link to={LOGIN} aria-label='login'>
                Login
              </Link>
            </Typography>
          )}
          {activeStep > 0 && (
            <Box className='goBack'>
              <Link onClick={handleBack} aria-label='back-button'>
                Go back
              </Link>
            </Box>
          )}
        </Box>
      </Box>
    </StyledSignup>
  );
};

export default SignUpForm;
